import { useEffect, useRef, useState } from "react";
import type { Brand, Domain } from "../api/types.ts";
import { useSendingIdentity } from "./SendingIdentity.tsx";

/**
 * Picks which company the app is looking at.
 *
 * A brand rather than a sender, because that is the unit an operator thinks in:
 * PayKings, Pulse and Payment Review are separate businesses with separate
 * lists, separate consent and separate reputations. Choosing one scopes the
 * dashboard to it, and the sending identity follows — a sender belongs to a
 * domain, which belongs to exactly one brand.
 *
 * "All companies" stays available, because the first question on a Monday is
 * usually whether anything is wrong anywhere rather than how one brand did.
 */
export function BrandSwitcher({ brands, domains }: { brands: Brand[]; domains: Domain[] }) {
  const { brandId, setBrandId } = useSendingIdentity();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = brands.find((b) => b.id === brandId) ?? null;
  const domainCount = (id: string) => domains.filter((d) => d.brandId === id).length;

  /**
   * Dismiss on an outside pointer press or Escape. Testing containment against
   * a ref avoids putting a click handler on the panel purely to stop
   * propagation, which would be a non-interactive element with a mouse-only
   * affordance.
   */
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (brands.length === 0) {
    return <span className="pill mute">No companies configured</span>;
  }

  const allDomains = domains.length;

  return (
    <div ref={rootRef} style={{ display: "contents" }}>
      <button
        type="button"
        className="switcher"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span
          className="badge"
          style={{
            background: selected?.color ?? "var(--dark)",
            color: selected ? "#fff" : "var(--green)",
          }}
        >
          {selected ? selected.badge : "ALL"}
        </span>
        <span className="info">
          <span className="name">{selected ? selected.name : "All companies"}</span>
          <span className="addr">
            {selected
              ? `${domainCount(selected.id)} ${domainCount(selected.id) === 1 ? "domain" : "domains"}`
              : `${brands.length} companies · ${allDomains} domains`}
          </span>
        </span>
        <span className="chev">▾</span>
      </button>

      {open && (
        <div className="panel-pop" role="listbox" aria-label="Company">
          <div className="head">◆ Company</div>

          <button
            type="button"
            role="option"
            aria-selected={selected === null}
            className={`option${selected === null ? " selected" : ""}`}
            onClick={() => {
              setBrandId(null);
              setOpen(false);
            }}
          >
            <span className="badge" style={{ background: "var(--dark)", color: "var(--green)" }}>
              ALL
            </span>
            <span>
              <span className="name">All companies</span>
              <span className="addr" style={{ display: "block" }}>
                Everything, across every brand
              </span>
            </span>
          </button>

          {brands.map((brand) => (
            <button
              key={brand.id}
              type="button"
              role="option"
              aria-selected={brand.id === selected?.id}
              className={`option${brand.id === selected?.id ? " selected" : ""}`}
              onClick={() => {
                setBrandId(brand.id);
                setOpen(false);
              }}
            >
              <span className="badge" style={{ background: brand.color, color: "#fff" }}>
                {brand.badge}
              </span>
              <span>
                <span className="name">{brand.name}</span>
                <span className="addr" style={{ display: "block" }}>
                  {domainCount(brand.id)} {domainCount(brand.id) === 1 ? "domain" : "domains"}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
